import { useMutation, useQuery } from '@apollo/client'
import { useRef } from 'react'
import {
  SetWaveExclusionsV2Mutation,
  SetWaveExclusionsV2MutationVariables,
  WaveExclusionsQuery,
  WaveExclusionsQueryVariables
} from '../../../data/gql-gen/questionnaire/graphql'
import { SET_WAVE_EXCLUSIONS_V2 } from '../../../data/gql/questionnaire/mutations/setWaveExclusionsV2'
import { WAVE_EXCLUSIONS } from '../../../data/gql/questionnaire/queries/waveExclusions'
import { LoggerErrorType } from '../../../data/logger'
import { captureApolloError } from '../../../utils/HelperFunctions'

/** The waves a wave de-duplicates against, as stored on its questionnaire. */
export const useWaveExclusions = (projectId: string, waveId: string) => {
  const { data, loading, error } = useQuery<
    WaveExclusionsQuery,
    WaveExclusionsQueryVariables
  >(WAVE_EXCLUSIONS, {
    context: { clientName: 'questionnaire' },
    variables: { projectId, waveId },
    fetchPolicy: 'cache-and-network',
    onError: (error) => {
      captureApolloError(LoggerErrorType.ApolloQuery, 'waveExclusions', error)
    }
  })

  return {
    // Null selection means none.
    excludedWaveIds: data?.draftQuestionnaire?.excludedWaveIds ?? [],
    loading: loading && !data,
    error: data?.draftQuestionnaire ? undefined : error
  }
}

type SavedExclusions = NonNullable<
  SetWaveExclusionsV2Mutation['setWaveExclusionsV2']
>

export const useSetWaveExclusions = (projectId: string) => {
  const [setWaveExclusionsMutation, { loading }] = useMutation<
    SetWaveExclusionsV2Mutation,
    SetWaveExclusionsV2MutationVariables
  >(SET_WAVE_EXCLUSIONS_V2, { context: { clientName: 'questionnaire' } })
  // Saves run one at a time so an older one can't land after a newer one.
  const queue = useRef<Promise<unknown>>(Promise.resolve())
  const newest = useRef<unknown>(null)

  // Resolves to null when a newer selection was queued behind this one: each
  // save syncs the wave's exclusion groups, so only the newest one is sent.
  const setWaveExclusions = (
    waveId: string,
    excludedWaveIds: string[]
  ): Promise<SavedExclusions | null> => {
    const request = {}
    newest.current = request
    const save = queue.current
      .catch(() => undefined)
      .then(async () => {
        if (newest.current !== request) {
          return null
        }

        const { data } = await setWaveExclusionsMutation({
          variables: { input: { projectId, waveId, excludedWaveIds } },
          update: (cache, { data: result }) => {
            const saved = result?.setWaveExclusionsV2
            if (!saved) {
              return
            }
            cache.updateQuery<
              WaveExclusionsQuery,
              WaveExclusionsQueryVariables
            >(
              { query: WAVE_EXCLUSIONS, variables: { projectId, waveId } },
              (cached) =>
                cached?.draftQuestionnaire
                  ? {
                      ...cached,
                      draftQuestionnaire: {
                        ...cached.draftQuestionnaire,
                        excludedWaveIds: saved.excludedWaveIds
                      }
                    }
                  : cached
            )
          }
        })

        if (!data?.setWaveExclusionsV2) {
          throw new Error('Wave exclusions not saved')
        }

        return data.setWaveExclusionsV2
      })
    queue.current = save
    return save
  }

  return { setWaveExclusions, loading }
}
